import { useEffect, useMemo, useState } from "react";

import type { DeviceType } from "@/components/filters/DeviceTypeFilter";
import i18n from "@/i18n/i18n";
import { AnalyticsServiceV2 } from "@/services/features/AnalyticsServiceV2";
import {
  AnalyticsCardData,
  DateRange,
  storePerformanceService,
} from "@/services/StorePerformanceService";
import { formatPercentFromWhole } from "@/utils/number";
import { getBrowserTimezone } from "@/utils/storeTime";
import { useLatestRequestGuard } from "@/hooks/useLatestRequestGuard";

export const useNamedShoppers = (
  storeId: number[] | null,
  dateRange?: DateRange,
  deviceType?: DeviceType,
  areaIds?: number[],
): AnalyticsCardData => {
  const areaIdsKey = areaIds?.join(",") ?? "";
  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  const browserTimezone = getBrowserTimezone();
  const [rawData, setRawData] = useState<{ total: number; named: number; namedPct: number } | null>(
    null,
  );
  const beginRequest = useLatestRequestGuard();

  useEffect(() => {
    const isCurrent = beginRequest();
    if (!storeId || !dateRange) {
      setRawData(null);
      return;
    }

    const fetchNamedShoppers = async () => {
      try {
        const analyticsService = AnalyticsServiceV2.getInstance();
        const params: any = {
          storeIds: storeId,
          startDate: storePerformanceService.formatDate(dateRange.start, browserTimezone),
          endDate: storePerformanceService.formatDate(dateRange.end, browserTimezone),
        };
        if (deviceType && deviceType !== "all") params.deviceType = deviceType;
        if (areaIdsKey) params.areaIds = areaIdsKey.split(",").map(Number);
        const response = await analyticsService.getSessionAnalytics(params);

        const total =
          deviceType === "IN_AISLE"
            ? (response?.data?.totalSessionsIncludingNoItems ?? 0)
            : (response?.data?.totalSessions ?? 0);
        const named = response?.data?.numberOfNamedShoppers ?? 0;

        if (total <= 0 || named < 0) {
          if (isCurrent()) setRawData(null);
          return;
        }

        const namedPct = total > 0 ? (named / total) * 100 : 0;
        if (isCurrent()) setRawData({ total, named, namedPct });
      } catch (error) {
        console.error("Error fetching named shoppers:", error);
        if (isCurrent()) setRawData(null);
      }
    };

    fetchNamedShoppers();
  }, [
    Array.isArray(storeId) ? storeId.join(",") : (storeId ?? null),
    browserTimezone,
    storePerformanceService.formatDate(dateRange?.start, browserTimezone),
    storePerformanceService.formatDate(dateRange?.end, browserTimezone),
    deviceType,
    areaIdsKey,
  ]);

  return useMemo(() => {
    if (!rawData) {
      return {
        previousValue: null,
        isPositive: true,
        pillText: null,
        subtitle: "",
        value: "--",
        rawValue: null,
        trendRawValue: null,
      };
    }

    return {
      previousValue: null,
      value: formatPercentFromWhole(rawData.namedPct, currentLanguage),
      subtitle: i18n.t("common.analytics.outOfAllShoppers"),
      isPositive: true,
      pillText: i18n.t("common.analytics.namedShoppersOfTotalSessions", {
        namedCount: rawData.named,
        totalCount: rawData.total,
      }),
      rawValue: rawData.namedPct,
      trendRawValue: rawData.namedPct,
    };
  }, [rawData, currentLanguage]);
};
